/**
 * RAG session identity (TRUST layer — Companion master-plan subsystem J).
 *
 * A stable per-browser-session id used to scope TEMPORARY (session) RAG
 * documents: uploads with scope='session' are indexed in the isolated vector
 * namespace `tmp:{tenant}:{sessionId}` and RAG queries passing the same id
 * see them on top of the permanent knowledge base. Other sessions never do.
 *
 * INTEGRATION POINT for the Companion shell / chat upload flow:
 *   import { getRagSessionId, endRagSession } from '@/lib/rag-session'
 *   - pass `getRagSessionId()` as `session_id` on RAG calls
 *     (/knowledge/entities/analyze, /knowledge/entities/search,
 *      /knowledge/docs/ingest)
 *   - call `endRagSession()` on chat-session end to purge that session's
 *     temporary docs server-side (DELETE /knowledge/docs/session/{id}).
 */

const KEY = 'sgrc-rag-session-id'

function makeId(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  }
}

/** Stable id for the current browser session (survives SPA navigation). */
export function getRagSessionId(): string {
  try {
    let id = sessionStorage.getItem(KEY)
    if (!id) {
      id = makeId()
      sessionStorage.setItem(KEY, id)
    }
    return id
  } catch {
    // Storage unavailable (private mode edge cases) — per-load fallback.
    return makeId()
  }
}

/** Rotate the session id locally (a new "session" starts). */
export function resetRagSessionId(): string {
  const id = makeId()
  try {
    sessionStorage.setItem(KEY, id)
  } catch {
    /* ignore */
  }
  return id
}

/**
 * End the current RAG session: purge its temporary docs server-side and
 * rotate the local id. Fire-and-forget safe (never throws).
 */
export async function endRagSession(): Promise<void> {
  const id = (() => {
    try {
      return sessionStorage.getItem(KEY)
    } catch {
      return null
    }
  })()
  resetRagSessionId()
  if (!id) return
  try {
    const { deleteSessionDocs } = await import('@/services/knowledge-docs.service')
    await deleteSessionDocs(id)
  } catch {
    // Server purge is best-effort — the hourly TTL job is the backstop.
  }
}
